import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@erp/ui';
import { ShoppingBag, Users, Package } from '@/components/icons';
import { useSalesDocuments } from '@/hooks/use-sales';
import { useOnlineCustomers, useStorefrontProducts } from '@/hooks/use-storefront';

function WidgetCard({ to, title, value, icon, subtitle }: { to: string; title: string; value: string | number; icon: React.ReactNode; subtitle?: string }) {
  return (
    <Link to={to}>
      <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">{title}</p>
          <div className="text-muted-foreground">{icon}</div>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-semibold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </CardContent>
      </Card>
    </Link>
  );
}

export function PublishedProductsWidget() {
  const { t } = useTranslation();
  const { data: products } = useStorefrontProducts({ isPublished: true });
  return <WidgetCard to="/storefront/products" title={t('storefront.widgets.publishedProducts')} value={products?.length ?? 0} icon={<Package size={16} />} />;
}

export function PendingOnlineOrdersWidget() {
  const { t } = useTranslation();
  const { data: orders } = useSalesDocuments({ docType: 'order' });
  const pending = (orders ?? []).filter((o) => o.channel === 'storefront' && !o.isInvoiced).length;
  return <WidgetCard to="/storefront/orders" title={t('storefront.widgets.pendingOrders')} value={pending} icon={<ShoppingBag size={16} />} />;
}

export function NewOnlineCustomersWidget() {
  const { t } = useTranslation();
  const { data: customers } = useOnlineCustomers();
  const since = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const count = (customers ?? []).filter((c) => new Date(c.createdAt).getTime() >= since).length;
  return <WidgetCard to="/storefront/customers" title={t('storefront.widgets.newCustomersThisWeek')} value={count} icon={<Users size={16} />} />;
}

export function OnlineSalesTodayWidget() {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const { data: orders } = useSalesDocuments({ docType: 'order' });
  const total = (orders ?? [])
    .filter((o) => o.channel === 'storefront' && o.docDate === today)
    .reduce((s, o) => s + Number(o.totalTtc || 0), 0);
  return (
    <WidgetCard
      to="/storefront/analytics"
      title={t('storefront.widgets.onlineSalesToday')}
      value={`${total.toFixed(3)} TND`}
      icon={<ShoppingBag size={16} />}
    />
  );
}
